// Source - https://stackoverflow.com/a/79637950
// Posted by Jasperan
// Retrieved 2026-03-19, License - CC BY-SA 4.0

//Slight modification to the useEffect for pointer: coarse media query

'use client'

import { createContext, PropsWithChildren, useContext, useSyncExternalStore } from 'react'
import { PopoverContentProps, PopoverProps, PopoverTriggerProps } from '@radix-ui/react-popover'
import {
  TooltipContentProps,
  TooltipProps,
  TooltipProviderProps,
  TooltipTriggerProps,
} from '@radix-ui/react-tooltip'

import { cn } from '@/utilities/ui'

import { Popover, PopoverContent, PopoverTrigger } from './popover'
import {
  Tooltip,
  TooltipContent,
  tooltipContentPopoverMotionClassName,
  tooltipContentSharedClassName,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'

const COARSE_POINTER_MQL = '(pointer: coarse)'

function subscribeCoarsePointer(onStoreChange: () => void) {
  const mql = window.matchMedia(COARSE_POINTER_MQL)
  mql.addEventListener('change', onStoreChange)
  return () => mql.removeEventListener('change', onStoreChange)
}

function getCoarsePointerSnapshot() {
  return window.matchMedia(COARSE_POINTER_MQL).matches
}

function getServerCoarsePointerSnapshot() {
  return false
}

const TouchContext = createContext<boolean | undefined>(undefined)
const useTouch = () => useContext(TouchContext)

const TouchProvider = (props: PropsWithChildren) => {
  const isCoarsePointer = useSyncExternalStore(
    subscribeCoarsePointer,
    getCoarsePointerSnapshot,
    getServerCoarsePointerSnapshot,
  )

  return <TouchContext.Provider value={isCoarsePointer} {...props} />
}

const HybridTooltipProvider = (props: TooltipProviderProps) => {
  return <TooltipProvider delayDuration={0} {...props} />
}

const HybridTooltip = (props: TooltipProps & PopoverProps) => {
  const isTouch = useTouch()

  return isTouch ? (
    <Popover delayDuration={0} {...props} />
  ) : (
    <Tooltip delayDuration={0} {...props} />
  )
}

const HybridTooltipTrigger = (props: TooltipTriggerProps & PopoverTriggerProps) => {
  const isTouch = useTouch()

  return isTouch ? <PopoverTrigger {...props} /> : <TooltipTrigger {...props} />
}

const HybridTooltipContent = (props: TooltipContentProps & PopoverContentProps) => {
  const isTouch = useTouch()
  const { className, ...rest } = props

  return isTouch ? (
    <PopoverContent
      className={cn(
        tooltipContentSharedClassName,
        tooltipContentPopoverMotionClassName,
        'w-auto max-w-[min(20rem,calc(100vw-2rem))] min-w-0 outline-none',
        className,
      )}
      {...rest}
    />
  ) : (
    <TooltipContent className={className} {...rest} />
  )
}

export {
  TouchProvider,
  HybridTooltipProvider,
  HybridTooltip,
  HybridTooltipTrigger,
  HybridTooltipContent,
}
